import { useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import type {
  Tab,
  TalentDiscoveryAiFilterSearchResponseV2,
} from '@revelio/data-access';

import type { PositionedItem } from '../CustomDash/components/PageConfigProvider/types/item';
import { getGlobalAssistantRouteAccess } from './global-ai-assistant-access';
import type {
  CustomDashAssistantEntityType,
  GlobalAssistantContextPolicy,
  GlobalAssistantSuggestMatch,
} from './global-ai-assistant-api';
import { useGlobalAiCommandStore } from './global-ai-assistant-command-store';
import {
  getSingleChartStandardPageMatch,
  getStandardPageMatchForOptionId,
} from './global-ai-assistant-standard-page-match';
import type { GlobalAiTranscriptMessage } from './global-ai-assistant-transcript';
import type { HeadlessGeneratedPage } from './global-ai-generate-headless';
import { isCancellation } from './global-ai-loop-error-telemetry';
import {
  buildBuildHereResumeSeed,
  buildNavResumeSeed,
} from './global-ai-system-seeds';
import { globalAssistantPathsMatch } from './global-assistant-route-filter-path';
import { hasOfferMarker, type OfferKind } from './offer-marker';
import { stashGeneratedPage } from './pending-generated-page';
import type { ReasoningTrace } from './reasoning-trace';
import { buildFilterResolutionDigest } from './turn-outcome-digest';

// Which chip kind each offer marker suppresses AND the transcript message
// `kind` that must back it for the inline trigger to render. Suppression is
// gated on this: a chip is hidden only when the stamped id resolves to a live
// assistant message of the matching kind (the trigger will actually render).
const KIND_BY_OFFER = {
  nav: 'nav_confirm',
  build: 'create_page_confirm',
  page: 'single_chart',
  report: 'report_offers',
  custom: 'single_chart',
  filters: 'single_chart',
  // 'geo_tax' (switch the geography taxonomy preference) has no chip and no
  // backing transcript message — it exists only as an inline narration
  // trigger — so there is nothing for it to suppress. `null` keeps the map
  // exhaustive over OfferKind so a future kind still has to declare itself.
  geo_tax: null,
} as const satisfies Record<OfferKind, string | null>;

export function useGlobalAiOfferWiring(args: {
  transcript: GlobalAiTranscriptMessage[];
  isTurnRunning: boolean;
  isCommandInFlight: boolean;
  userTabs: Tab[];
  navigateToMatch: (
    match: GlobalAssistantSuggestMatch,
    options?: {
      standard?: boolean;
      prompt?: string;
      skipFilterApply?: boolean;
      resolvedFilters?: TalentDiscoveryAiFilterSearchResponseV2;
      answerMessageId?: string;
    }
  ) => Promise<{ navigated: boolean }>;
  startCustomPageCreate: (
    entityType: CustomDashAssistantEntityType,
    options?: {
      prompt?: string;
      contextPolicy?: GlobalAssistantContextPolicy;
      skipBuild?: boolean;
      answerMessageId?: string;
    }
    // Async: the unsaved-changes guard can open the in-app dialog.
  ) => Promise<{ ok: boolean; targetPath?: string }>;
  /**
   * Lazy click-time headless page generation for build chips that carry NO
   * pre-generated layout. `page` resolves null on failure/questions → legacy
   * flow. On success, `buildTrace` is the click's ended build trace: the
   * landing narration reopens it so "Reading the page data" joins the same
   * working block instead of opening a second one.
   */
  generateForBuildClick: (
    prompt: string,
    entityType: CustomDashAssistantEntityType
  ) => Promise<{
    page: HeadlessGeneratedPage | null;
    buildTrace?: { traceId: string; trace: ReasoningTrace };
  }>;
  /**
   * Build the consume-once `onSeeded` callback a resolve-first build stashes
   * with its generated page: it arms the landing-narration collection
   * ("Reading the page data" step → digest-seeded silent narrate turn) once
   * `CustomDashRoute` reports the minted widget ids. See
   * seeded-build-narration.ts.
   */
  beginSeededBuildNarration: (params: {
    prompt: string;
    targetPathname: string;
    filters?: TalentDiscoveryAiFilterSearchResponseV2;
    resumeTrace?: { traceId: string; trace: ReasoningTrace };
  }) => (seededItems: PositionedItem[]) => void;
  markNavConfirmResolved: (id: string) => void;
  /**
   * Append a message straight to the transcript, no model turn. Used to tell
   * the user when a build's scope resolved to nothing — the same in-chat
   * surface the engine uses for a failed optimistic apply.
   */
  appendTranscript: (message: GlobalAiTranscriptMessage) => void;
  startAssistantTurn: (
    prompt: string,
    options?: { silent?: boolean; suppressReportOffer?: boolean }
  ) => Promise<void>;
  armNavResume: (next: {
    continuationPrompt: string;
    pageName: string;
    expectedPath: string;
  }) => void;
  armCreatePageResume: (next: { seed: string; expectedPath: string }) => void;
  armBuildHere: () => void;
  /**
   * Reveal + follow the chat to the bottom, called the instant an offer that
   * produces chat output is clicked. The offer's work (status → reasoning
   * trace → grounded narration) streams in at the bottom over many seconds; the
   * transcript pane owns the scroll, so it hands this in and the handlers fire
   * it on click for immediate feedback that the click did something.
   */
  beginActionFollow: () => void;
  /**
   * Engine action behind the inline `offer:geo_tax` trigger: write the GLOBAL
   * geography taxonomy preference, then re-resolve + re-apply under it.
   */
  switchGeographyTaxonomy: (
    nextTaxonomyId: number,
    prompt?: string,
    reaskOnly?: boolean
  ) => Promise<void>;
}) {
  const location = useLocation();

  // Offer lookup index, built per transcript change in TWO passes.
  // Narration triggers resolve their action by the `offerTargets` id STAMPED
  // on the text message by the client tool that appended it — never by
  // transcript position — so no adjacency/turn heuristic exists to go stale.
  // `stampedOfferKindsByTargetId` inverts the stamps PER-KIND (targetId -> set
  // of offer kinds). It drives chip suppression for the kinds that still HAVE a
  // chip — `nav` and `build` — where the confirm chip is hidden ONLY when a
  // narration stamped ITS id, carried that kind's marker, AND the stamped id
  // RESOLVES to a live assistant message of the matching kind, i.e. only when
  // the inline trigger will actually render. Suppression thus equals
  // renderability: a dangling or mistyped stamp (id absent, or resolving to the
  // wrong kind) can no longer hide a chip whose replacement trigger degrades to
  // plain text, so the user never gets neither a link nor a button. `page` has
  // no chip to suppress — the inline trigger is its only surface. Building
  // `byId` first (pass 1) lets pass 2 do this resolution.
  const { messagesById, stampedOfferKindsByTargetId } = useMemo(() => {
    // Pass 1: id -> message, so pass 2 can resolve each stamp's target.
    const byId = new Map<string, GlobalAiTranscriptMessage>();
    for (const m of args.transcript) {
      byId.set(m.id, m);
    }
    // Pass 2: validate each stamp against `byId` and record the suppression
    // only when the target is renderable (resolves to the matching kind).
    const stampedKinds = new Map<string, Set<OfferKind>>();
    for (const m of args.transcript) {
      if (
        m.role !== 'assistant' ||
        !('kind' in m) ||
        m.kind !== 'text' ||
        !m.offerTargets
      ) {
        continue;
      }
      for (const kind of Object.keys(KIND_BY_OFFER) as OfferKind[]) {
        const stampedKind = KIND_BY_OFFER[kind];
        const targetId = m.offerTargets[kind];
        if (!stampedKind || !targetId || !hasOfferMarker(m.text, kind)) {
          continue;
        }
        // Suppress the chip ONLY when the inline trigger can actually render:
        // the stamped id must resolve to a live assistant message of the
        // matching kind. A dangling/mistyped stamp keeps the chip as the
        // fallback affordance.
        const target = byId.get(targetId);
        const renderable =
          target &&
          target.role === 'assistant' &&
          'kind' in target &&
          target.kind === stampedKind;
        if (!renderable) {
          continue;
        }
        const kinds = stampedKinds.get(targetId) ?? new Set<OfferKind>();
        kinds.add(kind);
        stampedKinds.set(targetId, kinds);
      }
    }
    return {
      messagesById: byId,
      stampedOfferKindsByTargetId: stampedKinds,
    };
  }, [args.transcript]);

  // Fire the nav offer: the guarded navigate + resolve + arm/fire resume that
  // the `nav_confirm` chip performs. Shared by the chip and the inline narration
  // trigger so both fire the exact same action (no divergence). Only ever runs
  // on an explicit user click — nothing auto-navigates, on home or off-home.
  const navOfferInFlightRef = useRef(false);
  const fireNavOffer = async (
    navMsg: Extract<GlobalAiTranscriptMessage, { kind: 'nav_confirm' }>
  ) => {
    // Mid-stream guard, mirrors the chips' `isDisabled`: never fire while a
    // turn/command is in flight.
    if (args.isTurnRunning || args.isCommandInFlight) {
      return;
    }
    // Re-entry guard for the async body itself: the render-scoped
    // isCommandInFlight above can lag a rapid double-click (the closure was
    // created before the first click's command registered), so a ref makes
    // "disabled while loading" airtight without any visual state.
    if (navOfferInFlightRef.current) {
      return;
    }
    // Reveal immediately, but only for a nav that will actually produce chat
    // output — a compound nav resumes a turn on the destination. A pure page
    // nav (no continuationPrompt) adds nothing to the transcript, so there is
    // nothing to follow.
    if (navMsg.continuationPrompt) {
      args.beginActionFollow();
    }
    navOfferInFlightRef.current = true;
    try {
      await fireNavOfferInner(navMsg);
    } finally {
      navOfferInFlightRef.current = false;
    }
  };
  const fireNavOfferInner = async (
    navMsg: Extract<GlobalAiTranscriptMessage, { kind: 'nav_confirm' }>
  ) => {
    const navMatch = getStandardPageMatchForOptionId(navMsg.optionId, {});
    const access = navMatch
      ? getGlobalAssistantRouteAccess(navMatch.navigatePath, args.userTabs)
      : null;
    if (!navMatch || !access?.allowed) {
      return;
    }
    // `resolved` means the one-shot continuation resume was already consumed
    // — it must NOT kill the link itself. Navigation (+ the deterministic
    // filter re-apply) stays clickable forever, matching the offer:page
    // path and the persisted-history contract (a rehydrated chat's links
    // keep working).
    const resumeAlreadyConsumed = navMsg.resolved === true;
    const alreadyOnTarget = globalAssistantPathsMatch(
      location.pathname,
      navMatch.navigatePath
    );
    // The guarded navigation: the unsaved-changes redirect guard inside
    // navigateToMatch can CANCEL it. Only proceed (resolve + arm/fire the
    // resume) when navigation actually happened, so a cancelled click leaves
    // the offer live to retry.
    // DATA-GROUNDED nav (offer_page_or_custom with a filterArtifactId): the
    // message carries the answer's exact resolved artifact — apply IT on the
    // destination so the page shows the same slice the narrated figures came
    // from (text=link; landing on the page's default primary instead was the
    // live 2026-07-08 miss). Otherwise skipFilterApply: the `navigate` tool is
    // pure navigation — without it, navigateToMatch falls back to
    // lastUserPrompt and fires a stale discovery resolve+apply. Any
    // chained filtering belongs to the resumed turn (continuationPrompt),
    // which can call apply_filters properly.
    const { navigated } = await args.navigateToMatch(
      navMatch,
      navMsg.filters
        ? {
            standard: true,
            resolvedFilters: navMsg.filters,
            answerMessageId: navMsg.id,
          }
        : {
            standard: true,
            skipFilterApply: true,
            answerMessageId: navMsg.id,
          }
    );
    if (!navigated) {
      return;
    }
    // One-shot ONLY for compound navigations: the resumed continuation turn
    // below must never double-fire, so those mark resolved (line below). A
    // pure or data-grounded nav link (offer_page_or_custom's page link,
    // grounded navigate — no continuationPrompt) stays LIVE: it is never
    // marked, so nothing can kill it — re-clicking after browsing away simply
    // navigates (and re-applies the artifact) again.
    // `resumeAlreadyConsumed` extends that same liveness to COMPOUND links in
    // a rehydrated chat, where `resolved` persists across a reload: it gates
    // only the one-shot resume, never the navigation itself (which already
    // happened above). Spam while loading is blocked by the in-flight ref +
    // command guard at the top of fireNavOffer.
    if (!navMsg.continuationPrompt || resumeAlreadyConsumed) {
      return;
    }
    args.markNavConfirmResolved(navMsg.id);
    if (alreadyOnTarget) {
      // No navigation occurred (already on the destination), so the
      // path-keyed effect won't see a change — fire the resume immediately.
      // Context is already correct.
      void args.startAssistantTurn(
        buildNavResumeSeed({
          pageName: navMsg.pageName,
          continuationPrompt: navMsg.continuationPrompt,
        }),
        { silent: true }
      );
      return;
    }
    // ARM the resume; the location-keyed effect fires it once the router lands
    // on the destination, so the resumed turn reads the DESTINATION page
    // context (not the page we left).
    args.armNavResume({
      continuationPrompt: navMsg.continuationPrompt,
      pageName: navMsg.pageName,
      expectedPath: navMatch.navigatePath,
    });
  };

  // Single source for a single-value answer's standard-page offer state: the
  // derived page match and its route access. Shared by the inline trigger's
  // fire handler and by the text branch's trigger-liveness check, so the two
  // can never diverge on what counts as an offerable page.
  const getStandardPageOfferState = (
    chartMsg: Extract<GlobalAiTranscriptMessage, { kind: 'single_chart' }>
  ) => {
    const match = chartMsg.standardPagePath
      ? getSingleChartStandardPageMatch(chartMsg)
      : null;
    if (!match) {
      return null;
    }
    return {
      match,
      access: getGlobalAssistantRouteAccess(match.navigatePath, args.userTabs),
    };
  };

  // Fire the single-value page offer: navigate to the standard page backing a
  // single-value answer AND apply the question's filters. Invoked only by the
  // inline offer:page narration trigger. NOT auto-navigation: only ever runs on
  // an explicit click.
  const fireStandardPageOffer = async (
    chartMsg: Extract<GlobalAiTranscriptMessage, { kind: 'single_chart' }>
  ) => {
    if (args.isTurnRunning || args.isCommandInFlight) {
      return;
    }
    const offer = getStandardPageOfferState(chartMsg);
    if (!offer?.access.allowed) {
      return;
    }
    // Deterministic click: the single_chart message carries the EXACT resolved
    // artifact the answer was computed from (`filters`, appended by
    // `renderChartAndAwaitDigest`). Pass it through so the destination applies
    // that artifact directly — never an independent re-resolution of the
    // prompt, which can land on a completely different filter set than the
    // answer (e.g. taxonomy-31 "Nurse" 208k vs "Professionals" 3.7M). The
    // prompt-only fallback remains ONLY for legacy persisted messages that
    // predate the artifact.
    void args.navigateToMatch(offer.match, {
      standard: true,
      prompt: chartMsg.prompt,
      answerMessageId: chartMsg.id,
      ...(chartMsg.filters ? { resolvedFilters: chartMsg.filters } : {}),
    });
  };

  // Fire the build offer: the create-draft + navigate + arm/fire build resume
  // that the `create_page_confirm` chip performs. Shared by the chip and the
  // inline narration trigger. Build offers are NOT navigation — never auto-fired
  // on home; they stay explicit clicks.
  // Re-entry guard mirroring `navOfferInFlightRef`: the render-scoped
  // isTurnRunning/isCommandInFlight guards lag a rapid double-click (the
  // closure predates the first click's state flips), and the async body —
  // especially the LAZY click-time generation path, which runs for seconds —
  // must not run twice: a second entry would mint a second draft, re-fire
  // navigate(), and re-arm the one-shot resume, orphaning the first draft.
  const buildOfferInFlightRef = useRef(false);
  const fireBuildOffer = async (
    buildMsg: Extract<
      GlobalAiTranscriptMessage,
      { kind: 'create_page_confirm' }
    >
  ) => {
    // Reveal immediately on click — the build's "Opening custom page…" status
    // and, later, its streamed narration all land at the bottom; without this
    // the user sits scrolled up on the offer they clicked and sees nothing for
    // seconds. Safe to call ahead of the guards below: it only scrolls.
    args.beginActionFollow();
    if (buildOfferInFlightRef.current) {
      return;
    }
    buildOfferInFlightRef.current = true;
    try {
      await fireBuildOfferInner(buildMsg);
    } finally {
      buildOfferInFlightRef.current = false;
    }
  };
  const fireBuildOfferInner = async (
    buildMsg: Extract<
      GlobalAiTranscriptMessage,
      { kind: 'create_page_confirm' }
    >
  ) => {
    if (args.isTurnRunning || args.isCommandInFlight) {
      return;
    }
    // `resolved` only records that a legacy resume already ran once — a fresh
    // explicit click still rebuilds (fresh draft + fresh one-shot resume),
    // matching the pre-generated path's re-click semantics below.
    // RESOLVE-FIRST (Phase C): when the chip carries a PRE-GENERATED layout,
    // the click seeds a fresh draft with it and navigates — landing shows the
    // complete page, widgets data-load, and NO turn resumes (the generation
    // already happened during the offering turn). Filters ride the command
    // store's emit-before-navigate contract, claimed by the destination
    // bridge on mount. No generated payload → legacy confirm-navigate-resume.
    // Layout source: pre-generated during the offering turn
    // (create_custom_page), or generated LAZILY now (offer_page_or_custom's
    // secondary chip) — either way the navigation happens AFTER generation
    // (resolve-first), landing on a complete draft. Lazy failure → legacy.
    let lazy:
      | Awaited<ReturnType<typeof args.generateForBuildClick>>
      | undefined;
    try {
      lazy = buildMsg.generated
        ? undefined
        : await args.generateForBuildClick(
            buildMsg.prompt,
            buildMsg.entityType
          );
    } catch (err) {
      // An ABORTED click-time build throws (generateDashboardHeadless rethrows
      // its AbortError). Abandon quietly — falling through would run the legacy
      // resume flow and navigate the user into a build they just cancelled.
      //
      // Uses the shared predicate rather than a bare `instanceof DOMException`:
      // the rethrow forwards whatever the fetch layer produced, which is a
      // plain `Error` under some polyfills and carries `signal.reason` (a
      // string here) verbatim on a reasoned abort.
      if (isCancellation(err)) {
        return;
      }
      // Anything else is a real failure, which must keep the legacy
      // confirm-navigate-resume fallback rather than dead-clicking the chip.
      lazy = undefined;
    }
    const generatedPage = buildMsg.generated ?? lazy?.page ?? null;
    // The lazy click's build trace — the landing narration's reading step
    // resumes it (one click, one working block). The pre-generated chip has
    // no click-time trace, so its reading step opens its own small block.
    const buildTrace = lazy?.buildTrace;
    if (generatedPage) {
      const { ok, targetPath } = await args.startCustomPageCreate(
        buildMsg.entityType,
        {
          prompt: buildMsg.prompt,
          skipBuild: true,
          answerMessageId: buildMsg.id,
        }
      );
      if (!ok || !targetPath) {
        return;
      }
      // The draft path carries the current SEARCH string ("/company?filters=…")
      // but both consumers match on the bare pathname — the stash store
      // normalizes internally, and the bridge's applyRouteFilters claim
      // compares against location.pathname, so the command MUST be emitted
      // with the pathname or it is never claimed (verified live: layout
      // landed, Google filter silently didn't).
      const targetPathname = targetPath.split('?')[0] ?? targetPath;
      // Landing filters: page-gen's resolution if it resolved anything, else
      // the offer's grounded artifact. BOTH must be screened for the
      // "successfully empty" resolver result (empty expr + named entity in
      // unknownFilters — observed: "Ford" unmatched, "Mag 7" nondeterministic).
      // The apply runs `replace` + `preserveCurrentPrimaryWhenMissing: false`,
      // so an empty set can't express "no filter" — it only CLOBBERS the draft
      // onto the page default (the "Mag 7 → confident Workday dashboard" bug).
      // Collapsing each empty case to `undefined` first is what keeps the `??`
      // from ever landing an unresolved artifact.
      const generatedFilterDigest = generatedPage.filters
        ? buildFilterResolutionDigest({
            response: generatedPage.filters,
            targetPage: targetPathname,
          })
        : null;
      const generatedResolvedSomething =
        (generatedFilterDigest?.applied.length ?? 0) > 0;
      const resolvedGeneratedFilters = generatedResolvedSomething
        ? generatedPage.filters
        : undefined;
      const offerFilterDigest = buildMsg.filters
        ? buildFilterResolutionDigest({
            response: buildMsg.filters,
            targetPage: targetPathname,
          })
        : null;
      const resolvedOfferFilters =
        (offerFilterDigest?.applied.length ?? 0) > 0
          ? buildMsg.filters
          : undefined;
      const landingFilters = resolvedGeneratedFilters ?? resolvedOfferFilters;
      // Stash BEFORE React processes the route change (same tick): the
      // mounting NewCustomPage consumes it and seeds the draft config. The
      // onSeeded callback restores the GROUNDED landing narration the legacy
      // resume flow had: once the consumer reports the minted widget ids, the
      // drawer collects their settled digests ("Reading the page data") and
      // narrates the finished page from them.
      stashGeneratedPage(targetPathname, {
        content: generatedPage.content,
        ...(landingFilters ? { filters: landingFilters } : {}),
        prompt: buildMsg.prompt,
        onSeeded: args.beginSeededBuildNarration({
          prompt: buildMsg.prompt,
          targetPathname,
          ...(landingFilters ? { filters: landingFilters } : {}),
          ...(buildTrace ? { resumeTrace: buildTrace } : {}),
        }),
      });
      if (landingFilters) {
        useGlobalAiCommandStore.getState().emit({
          kind: 'applyRouteFilters',
          requestId: crypto.randomUUID(),
          filterKind: 'tdV2',
          response: landingFilters,
          targetPath: targetPathname,
          entityType: buildMsg.entityType,
          prompt: buildMsg.prompt,
        });
      }
      // When nothing resolved the draft lands on defaults, and an authoritative
      // layout with the wrong company must not go unremarked. Union + dedupe
      // both digests' unresolved names (screened: the digest can `JSON.stringify`
      // a non-string name, safe for the model but never for chat).
      const unresolvedScopeLabels = [
        ...new Set(
          [
            ...(generatedFilterDigest?.unresolved ?? []),
            ...(offerFilterDigest?.unresolved ?? []),
          ].map((item) => item.requested)
        ),
      ].filter(
        (label) =>
          label.trim().length > 0 &&
          label.length <= 80 &&
          !label.startsWith('{')
      );
      // Gate on `landingFilters`, not on how it was derived: this must say
      // "we emitted no apply", which is exactly the emit guard's condition.
      if (unresolvedScopeLabels.length > 0 && !landingFilters) {
        const quoted = unresolvedScopeLabels
          .map((label) => `"${label}"`)
          .join(', ');
        args.appendTranscript({
          id: crypto.randomUUID(),
          role: 'assistant',
          kind: 'text',
          text: `I couldn't match ${quoted} to a filter I can apply, so this dashboard shows the default view. Try being more specific and I'll rebuild it.`,
        });
      }
      // Do NOT mark this chip resolved: the finished layout persists on the
      // message (or regenerates for the lazy path), so the build stays a
      // repeatable action. A draft is unsaved — after the user navigates away it
      // is gone, and clicking the chip again must rebuild + navigate (a fresh
      // draft), not dead-no-op. Only the legacy resume path below marks resolved
      // (its click arms a one-shot turn resume that must not double-fire).
      return;
    }
    // Confirm-navigate-resume: the click IS the confirmation. (1) Create an
    // empty draft + navigate to it WITHOUT emitting the build. (2) ARM the
    // build-here ref so the resumed turn's `create_custom_page` call BUILDS on
    // this draft (CREATE semantics) instead of offering again. (3) Resume the
    // loop turn (silently) seeded with the full original prompt + a "you're on
    // a new empty page, build it here by calling create_custom_page" system
    // note, so the model runs the CREATE page-gen on the draft. Using CREATE —
    // not update_current_page — keeps the model's reasoning/narration framed as
    // a fresh build, not a modification.
    const { ok, targetPath } = await args.startCustomPageCreate(
      buildMsg.entityType,
      {
        prompt: buildMsg.prompt,
        skipBuild: true,
        answerMessageId: buildMsg.id,
      }
    );
    if (!ok || !targetPath) {
      return;
    }
    const seed = buildBuildHereResumeSeed(buildMsg.prompt);
    if (globalAssistantPathsMatch(location.pathname, targetPath)) {
      // The router already reflects the draft path (no further navigation
      // re-render will fire the path-keyed effect), so arm the build-here
      // signal and resume now — the loop's `create_custom_page` handle
      // consumes it and builds the draft (mode: 'create', no nav) rather
      // than offering again. Context is already the empty draft. The
      // bracketed seed is a system instruction — fire it silently so it
      // never renders as a user chat bubble. Revealing the resumed turn is
      // centralized: the transcript pane's silent-turn-start effect jumps to
      // the bottom for every link-fired action, this path included.
      args.armBuildHere();
      void args.startAssistantTurn(seed, {
        silent: true,
        suppressReportOffer: true,
      });
      return;
    }
    // ARM the resume; the location-keyed effect fires it once the router lands
    // on the empty draft, so the resumed turn reads the draft's context
    // (in_custom_dash_edit_mode). The effect arms the build-here signal
    // itself when it consumes this resume — the engine's continuation slot
    // holds ONE pending continuation at a time, so the build signal is
    // handed along rather than armed here alongside the resume.
    args.armCreatePageResume({
      seed,
      expectedPath: targetPath,
    });
  };

  // Fire the geography-taxonomy switch: the ONLY offer with no chip
  // counterpart — the account-wide preference change is disclosed and offered
  // purely as inline narration words, so the click IS the user's consent.
  // `nextTaxonomyId` was read from the turn's `filter_response` artifact by
  // the transcript (via the `offerTargets.geo_tax` stamp), never from the
  // phrase the model wrote.
  // `prompt` is the ask that EARNED this offer, carried on the narration
  // message: the switch re-resolves filters, and an older offer clicked after
  // a newer ask must re-resolve its own ask, not the newer one.
  const fireGeoTaxOffer = (
    nextTaxonomyId: number,
    prompt?: string,
    reaskOnly?: boolean
  ) => {
    // Same mid-stream guard as every other fire handler: never mutate the
    // account preference (or re-resolve) while a turn/command is in flight.
    if (args.isTurnRunning || args.isCommandInFlight) {
      return;
    }
    // The switch narrates + re-resolves at the bottom of the chat over
    // several seconds; reveal it the instant the words are clicked.
    args.beginActionFollow();
    void args.switchGeographyTaxonomy(nextTaxonomyId, prompt, reaskOnly);
  };

  return {
    messagesById,
    stampedOfferKindsByTargetId,
    fireNavOffer,
    fireBuildOffer,
    fireStandardPageOffer,
    fireGeoTaxOffer,
    getStandardPageOfferState,
  };
}
